import React from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaMobileAlt, FaPrint, FaChevronUp, FaLink } from "react-icons/fa";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: d },
});

const sections = [
  { id: "program", label: "Program Overview" },
  { id: "opt-in", label: "Opt-In Methods" },
  { id: "types-frequency", label: "Message Types & Frequency" },
  { id: "stop-help", label: "STOP & HELP" },
  { id: "delivery", label: "Delivery & Carriers" },
  { id: "eligibility", label: "Eligibility & Responsibilities" },
  { id: "privacy", label: "Privacy" },
  { id: "changes", label: "Changes" },
  { id: "contact", label: "Contact" },
];

export default function SMSTerms() {
  const onTocClick = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const anchor = (id) => (
    <a
      href={`#${id}`}
      onClick={(e) => { e.preventDefault(); onTocClick(id); }}
      className="ml-2 inline-flex items-center text-xs text-[#002b5c]/50 hover:text-[#002b5c]"
      aria-label="Copy link to section"
      title="Copy link to section"
    >
      <FaLink />
    </a>
  );

  return (
    <div className="bg-white text-[#002b5c]">
      <Helmet>
        <title>SMS Terms | Your Choice Associates</title>
        <meta
          name="description"
          content="SMS program terms — opt-in on Contact & Partner Apply, message types, STOP/HELP, delivery, and privacy."
        />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src="/images/hero.png" alt="" className="w-full h-[220px] md:h-[260px] object-cover" />
  <div className="absolute inset-0 bg-gradient-to-b from-[#002b5c]/90 to-[#002b5c]/75" />


        <div className="absolute inset-0 flex items-end pb-8">
          <div className="w-full px-6">
            <div className="max-w-6xl mx-auto text-white">
              <motion.div {...fade(0)} className="flex items-center gap-3 text-white/90">
                <span className="text-xs uppercase tracking-wider">Legal</span>
                <span className="text-white/40">•</span>
                <span className="text-xs">Effective: <strong>August 14, 2025</strong></span>
              </motion.div>
              <motion.h1 {...fade(0.08)} className="text-3xl md:text-5xl font-extrabold mt-2">
                SMS Terms & Conditions
              </motion.h1>
              <motion.div {...fade(0.16)} className="mt-5 flex items-center gap-3">
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-sm"
                >
                  <FaPrint /> Print
                </button>
                <span className="inline-flex items-center gap-2 text-xs bg-white/10 px-3 py-1.5 rounded-full">
                  <FaMobileAlt /> STOP / HELP supported
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="px-6 py-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-10">
          {/* TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 p-4 bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm">
              <p className="text-sm font-semibold mb-3">On this page</p>
              <nav className="space-y-2">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => onTocClick(s.id)}
                    className="block w-full text-left text-sm text-[#002b5c]/80 hover:text-[#001b35]"
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <div>
            <div className="text-sm text-[#002b5c]/70 mb-6">
              <Link to="/" className="hover:underline">Home</Link> <span className="mx-1">/</span> SMS Terms
            </div>

            <section id="program" className="mb-8">
              <motion.div {...fade(0)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Program Overview {anchor("program")}
                </h2>
                <p className="mt-4 text-gray-700">
                  By opting in to SMS from <strong>Your Choice Associates</strong> (“YCA”), you agree to these terms.
                  Our SMS program supports:
                </p>
                <ul className="mt-3 list-disc pl-6 text-gray-700 space-y-1">
                  <li>
                    <strong>Consumers:</strong> confirmations, follow-ups, and appointment reminders for requests
                    submitted on the <Link className="underline" to="/contact">Contact</Link> page.
                  </li>
                  <li>
                    <strong>Brokers:</strong> lead assignment alerts and account reminders for licensed brokers enrolled via{" "}
                    <Link className="underline" to="/partner-apply">Partner Apply</Link>.
                  </li>
                </ul>
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
                  We do <strong>not</strong> collect SMS consent on the Questionnaire page and do <strong>not</strong> send SMS from there.
                </div>
              </motion.div>
            </section>

            <section id="opt-in" className="mb-8">
              <motion.div {...fade(0.04)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Opt-In Methods {anchor("opt-in")}
                </h2>
                <ul className="mt-4 list-disc pl-6 text-gray-700 space-y-2">
                  <li>
                    <strong>Consumers (Contact):</strong> a required consent checkbox appears on{" "}
                    <Link className="underline" to="/contact">/contact</Link>. Submitting the form with the checked box
                    constitutes consent to receive SMS related to your request.
                  </li>
                  <li>
                    <strong>Brokers (Partner Apply):</strong> a required consent checkbox appears on{" "}
                    <Link className="underline" to="/partner-apply">/partner-apply</Link>. Submitting with the checked
                    box enables lead alerts and account-related texts.
                  </li>
                </ul>
                <p className="mt-3 text-gray-700">
                  Consent is not a condition of purchase. Message and data rates may apply.
                </p>
              </motion.div>
            </section>

            <section id="types-frequency" className="mb-8">
              <motion.div {...fade(0.08)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Message Types & Frequency {anchor("types-frequency")}
                </h2>
                <ul className="mt-4 list-disc pl-6 text-gray-700 space-y-1">
                  <li>Contact confirmations, follow-ups, appointment reminders (consumers).</li>
                  <li>Lead assignment alerts and reminders (brokers).</li>
                </ul>
                <p className="mt-3 text-gray-700">Message frequency varies based on activity.</p>
              </motion.div>
            </section>

            <section id="stop-help" className="mb-8">
              <motion.div {...fade(0.12)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  STOP & HELP {anchor("stop-help")}
                </h2>
                <p className="mt-4 text-gray-700">
                  Reply <strong>STOP</strong> to cancel SMS. Reply <strong>HELP</strong> for help. After STOP, we send a
                  confirmation and no longer text that number unless you opt in again.
                </p>
              </motion.div>
            </section>

            <section id="delivery" className="mb-8">
              <motion.div {...fade(0.16)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Delivery & Carriers {anchor("delivery")}
                </h2>
                <p className="mt-4 text-gray-700">
                  Delivery depends on your carrier and device. Carriers are not liable for delayed or undelivered messages.
                  Our program uses registered 10DLC where applicable.
                </p>
              </motion.div>
            </section>

            <section id="eligibility" className="mb-8">
              <motion.div {...fade(0.2)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Eligibility & Responsibilities {anchor("eligibility")}
                </h2>
                <p className="mt-4 text-gray-700">
                  You must be the owner or authorized user of the provided mobile number and at least 18 years old.
                  Keep your contact information accurate and up to date.
                </p>
              </motion.div>
            </section>

            <section id="privacy" className="mb-8">
              <motion.div {...fade(0.24)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Privacy {anchor("privacy")}
                </h2>
                <p className="mt-4 text-gray-700">
                  See our <Link className="underline" to="/privacy">Privacy Policy</Link> for how we handle phone numbers,
                  message content, and metadata needed to operate the program and comply with law. We do not sell personal information.
                </p>
              </motion.div>
            </section>

            <section id="changes" className="mb-8">
              <motion.div {...fade(0.28)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Changes {anchor("changes")}
                </h2>
                <p className="mt-4 text-gray-700">
                  We may update these SMS Terms. The effective date above reflects the latest revision.
                </p>
              </motion.div>
            </section>

            <section id="contact" className="mb-4">
              <motion.div {...fade(0.32)} className="bg-white border border-[#002b5c]/10 rounded-2xl shadow-sm p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-extrabold">
                  Contact {anchor("contact")}
                </h2>
                <p className="mt-4 text-gray-700">
                  For support, email{" "}
                  <a href="mailto:support@yourchoiceassociates.org" className="underline">
                    support@yourchoiceassociates.org
                  </a>{" "}
                  or call (917) 902-4471.
                </p>
              </motion.div>
            </section>

            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="fixed bottom-6 right-6 z-10 inline-flex items-center gap-2 rounded-full shadow-lg bg-[#002b5c] text-white px-4 py-3 hover:bg-[#001b35]"
              aria-label="Back to top"
            >
              <FaChevronUp /> Top
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
